import * as microsoftTeams from '@microsoft/teams-js';

/**
 * Bridges the Teams removal handler, which must exist before authentication
 * finishes, to the removal page, which cannot run until it does.
 *
 * Teams enables its Remove button five seconds after loading the page and
 * invokes whatever handler is registered at that moment. If none is registered,
 * teams-js reports success on our behalf and the tab disappears with the
 * incident still linked. Registering here -- before the SSO round trip that
 * gates the React tree -- closes that window, and the handler then waits for
 * the page to publish a worker, which is safe inside Teams' fifteen second
 * response budget.
 */

export type RemoveOutcome =
  | { disconnected: true }
  | { disconnected: false; message: string };

export type RemoveWorker = () => Promise<RemoveOutcome>;

const RESPONSE_BUDGET_MS = 12000;

// Held in a mutable slot rather than a resolved promise: the removal frame can mount more than once per JS context,
// and a settled promise would pin the first worker forever along with the incident id it captured.
let currentWorker: RemoveWorker | undefined;
let onWorkerPublished: (() => void) | undefined;

/** Called by the removal page once it can reach COMS. */
export function provideRemoveWorker(worker: RemoveWorker): void {
  currentWorker = worker;
  onWorkerPublished?.();
}

function awaitWorker(): Promise<RemoveWorker> {
  if (currentWorker) return Promise.resolve(currentWorker);

  return new Promise((resolve) => {
    onWorkerPublished = () => {
      if (currentWorker) resolve(currentWorker);
    };
  });
}

export function registerTeamsRemoveHandler(): void {
  try {
    microsoftTeams.pages.config.registerOnRemoveHandler((removeEvent) => {
      // teams-js throws if the event is notified twice, and the second outcome would mask the first.
      let settled = false;

      const succeed = () => {
        if (settled) return;
        settled = true;
        removeEvent.notifySuccess();
      };

      const fail = (message: string) => {
        if (settled) return;
        settled = true;
        // Teams removes the tab either way; this only decides what the user is told.
        removeEvent.notifyFailure(message);
      };

      const watchdog = window.setTimeout(() => {
        console.error(
          'PRATUS: the removal page did not respond in time, so the tab was removed without disconnecting the incident.'
        );
        succeed();
      }, RESPONSE_BUDGET_MS);

      void (async () => {
        try {
          const worker = await awaitWorker();
          const outcome = await worker();

          if (outcome.disconnected) {
            succeed();
            return;
          }
          fail(outcome.message);
        } catch (error) {
          console.error(
            'PRATUS: the removal worker threw. The tab was still removed.',
            error
          );
          succeed();
        } finally {
          window.clearTimeout(watchdog);
        }
      })();
    });

    // Teams keeps validity false until asked otherwise, and will not invoke the remove handler until it is true.
    microsoftTeams.pages.config.setValidityState(true);
  } catch (error) {
    console.warn('PRATUS: could not register the Teams remove handler.', error);
  }
}
